// Shared gear/item math — the ONE place item-level stat scaling, rarity colour,
// slot labels and upgrade/repair pricing live. Imported by Inventory.vue,
// ItemModal.vue, HeroCard.vue and HeroDossier.vue so none of them duplicate
// this formula (it must always match the backend's combat math exactly).
import type { Artifact, ArtifactRarity, EquipSlot } from './inventory-api';

/** Slot → display label (Dominion-style flavor names). */
export const SLOT_LABELS: Record<EquipSlot, string> = {
  weapon: 'Weapon', helmet: 'Hat', armor: 'Suit',
  boots: 'Shoes', ring: 'Ring', amulet: 'Charm',
};

/** Rarity ladder → accent hex (grey → green → violet → orange → gold). Drives
 *  card accent rails, glows, name colour and rarity tags everywhere. */
export const RARITY_HEX: Record<ArtifactRarity, string> = {
  COMMON: '#9a927f',
  UNCOMMON: '#6bbf59',
  RARE: '#b98cff',
  EPIC: '#e0872f',
  LEGENDARY: '#f0cf62',
};

export const MAX_ARTIFACT_LEVEL = 5;

/** Cash cost to upgrade FROM a given level (mirrors backend UPGRADE_COSTS in
 *  backend/src/game/inventory/inventory.constants.ts). */
export const UPGRADE_COST: Record<number, number> = { 1: 50, 2: 150, 3: 400, 4: 1000 };

/**
 * Level-scaled item stat (MUST match backend scaledArtifactStat in
 * backend/src/game/battle/battle-engine.ts): +10% of base per level above 1,
 * floored at +1 per level — the flat rounded multiplier plateaued small stats
 * (base 5 went 5,5,6,7,7) and made the last upgrade a no-op.
 */
export function scaledStat(stat: number, level: number): number {
  if (stat <= 0) return 0;
  return stat + Math.max(1, Math.round(stat * 0.1)) * (level - 1);
}

export type ItemStatKey = 'attack' | 'defense' | 'magic' | 'speed' | 'hp';

/** Display metadata for the four combat stats — label + accent used for bars,
 *  dossier tiles, item stat rows and gear-bonus rows across the whole page. */
export const STAT_META: Record<'attack' | 'defense' | 'magic' | 'speed', { label: string; short: string; color: string }> = {
  attack: { label: 'Firepower', short: 'FPR', color: '#c0504d' },
  defense: { label: 'Toughness', short: 'TUF', color: '#7aa0c8' },
  magic: { label: 'Cunning', short: 'CUN', color: '#b98cff' },
  speed: { label: 'Reflexes', short: 'RFX', color: '#7fbf8f' },
};

/** All five level-scaled stat contributions of an artifact, keyed for iteration. */
export function scaledStats(a: Artifact): Record<ItemStatKey, number> {
  return {
    attack: scaledStat(a.attack, a.level),
    defense: scaledStat(a.defense, a.level),
    magic: scaledStat(a.magic, a.level),
    speed: scaledStat(a.speed, a.level),
    hp: scaledStat(a.hp, a.level),
  };
}

export interface StatCompareRow {
  key: ItemStatKey;
  label: string;
  candidate: number;
  equipped: number;
  delta: number;
}

/**
 * Compare a candidate artifact's level-scaled stats against whatever currently
 * occupies its slot (or null if the slot is empty). Only rows where either side
 * is nonzero are returned. Powers the "compare vs equipped" delta list in the
 * item detail modal.
 */
export function compareStats(candidate: Artifact, equipped: Artifact | null): StatCompareRow[] {
  const cand = scaledStats(candidate);
  const equip = equipped ? scaledStats(equipped) : { attack: 0, defense: 0, magic: 0, speed: 0, hp: 0 };
  const labels: Record<ItemStatKey, string> = {
    attack: STAT_META.attack.label, defense: STAT_META.defense.label,
    magic: STAT_META.magic.label, speed: STAT_META.speed.label, hp: 'Health',
  };
  const keys: ItemStatKey[] = ['attack', 'defense', 'magic', 'speed', 'hp'];
  return keys
    .filter((k) => cand[k] !== 0 || equip[k] !== 0)
    .map((k) => ({ key: k, label: labels[k], candidate: cand[k], equipped: equip[k], delta: cand[k] - equip[k] }));
}

/** True when a breakable item is worn (below max) or fully broken. */
export function needsRepair(a: Artifact): boolean {
  if (!a.isBreakable || a.maxDurability == null) return false;
  return a.isBroken || (a.durability ?? a.maxDurability) < a.maxDurability;
}

/** Durability as a 0–100 percentage (broken → 0), for the wear bar. */
export function durabilityPct(a: Artifact): number {
  if (!a.isBreakable || a.maxDurability == null) return 100;
  if (a.isBroken) return 0;
  return Math.round(((a.durability ?? a.maxDurability) / a.maxDurability) * 100);
}

const RARITY_REPAIR_BASE: Record<ArtifactRarity, number> = {
  COMMON: 20, UNCOMMON: 45, RARE: 90, EPIC: 180, LEGENDARY: 400,
};

/**
 * Cash cost to fully repair an item — a client-side PREVIEW only (mirrors
 * backend/src/game/loot/loot-generator.ts:repairCost exactly). The server
 * remains the source of truth and re-derives this cost itself on /gear/repair;
 * this is purely so the modal can show a price before the player commits.
 */
export function repairCostPreview(a: Artifact): number {
  if (!a.isBreakable || a.maxDurability == null) return 0;
  const current = a.durability ?? a.maxDurability;
  const missing = a.maxDurability - current;
  if (missing <= 0 && !a.isBroken) return 0;
  const effectiveMissing = a.isBroken ? a.maxDurability : missing;
  const base = RARITY_REPAIR_BASE[a.rarity];
  const levelFactor = 1 + Math.max(0, a.level - 1) * 0.1;
  const wearFraction = Math.min(1, effectiveMissing / a.maxDurability);
  return Math.max(1, Math.ceil(base * levelFactor * wearFraction));
}
